<script setup lang="ts">
import dayjs from "dayjs";
import Modal from "@/ui/components/modal.vue";
import ComplexPriceItemCurveApplicationStatsDTO = Modules.BookingExperts.Data.MasterPriceList.ComplexPriceItemCurveApplicationStatsDTO;
import { onUnmounted, computed } from "vue";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import {
	colorForFactor,
	colorForAbsoluteOffset,
} from "@/modules/booking_experts/utils";
import ChartQuickView from "@/modules/booking_experts/pages/Components/Chart/ChartQuickView.vue";
import StatField from "@/modules/booking_experts/pages/Components/Chart/StatField.vue";

const { showDateControl = true, curveData } = defineProps<{
	/** Show the arrow buttons to navigate the check-in date. */
	showDateControl?: boolean;
	curveData?: {
		id: string;
		name: string;
	};
}>();
const stats = defineModel<ComplexPriceItemCurveApplicationStatsDTO>();
const visible = defineModel<boolean>("visible");
const emits = defineEmits(["day:plus", "day:minus"]);

const isLoading = computed(() => !stats.value);
const factor = computed(() => stats.value?.factor);
const absoluteOffset = computed(() => stats.value?.absolute_offset);
const isAbsolute = computed(() => absoluteOffset.value != null);

const formattedCheckInDate = computed(() => {
	if (isLoading.value || !stats.value) return "";
	return dayjs(stats.value.check_in).format("MMM D, YYYY");
});

const store = useCurveModelStore();
onUnmounted(() => {
	store.currentOccupancy = null;
	store.currentCurve = undefined;
	store.referenceData.y0 = store.referenceData.y1 = [];
});
</script>

<template>
	<modal
		v-model:visible="visible"
		@update:visible="() => (stats = undefined)"
		title="Applied Curve"
		icon="fa-solid fa-eye"
		width="600"
	>
		<v-card class="border mb-2" variant="flat">
			<v-card-title class="pa-2 curve-card-header bg-background">
				<div class="curve-title">
					<v-icon
						icon="fa-solid fa-bars"
						size="20"
						color="primary"
						class="mr-2"
					/>
					<span class="text-h6 font-weight-medium">Curve Statistics</span>
				</div>
				<div class="date-section">
					<span
						v-if="!isLoading"
						class="text-caption text-medium-emphasis mr-2"
					>
						Last updated:
						{{ dayjs(stats.applied_date).format("MMM D, YYYY HH:mm") }}
					</span>
					<v-skeleton-loader
						v-else
						type="text"
						width="180"
						height="30"
						class="text-caption bg-background"
					/>
				</div>
			</v-card-title>

			<!-- Content grid with right-aligned values -->
			<div class="stats-grid px-4">
				<!-- Pricing adjustment: relative factor or absolute offset -->
				<stat-field
					v-if="!isAbsolute"
					label="Current Pricing Factor"
					icon="fa-solid fa-percent"
					:value="factor"
					:is-loading="isLoading"
					type="factor"
					:color="colorForFactor(factor)"
					:skeleton-width="90"
				/>
				<stat-field
					v-else
					label="Current Price Adjustment"
					icon="fa-solid fa-euro-sign"
					:value="
						absoluteOffset != null
							? absoluteOffset >= 0
								? `+${absoluteOffset}€`
								: `${absoluteOffset}€`
							: 'N/A'
					"
					:is-loading="isLoading"
					type="text"
					:color="colorForAbsoluteOffset(absoluteOffset)"
					:skeleton-width="90"
				/>

				<!-- Occupancy -->
				<stat-field
					label="Current Occupancy"
					icon="fa-solid fa-bed"
					:value="stats?.occupancy"
					unit="%"
					:is-loading="isLoading"
					type="text"
					:skeleton-width="75"
				/>

				<!-- Selected Date -->
				<stat-field
					label="Selected Check-in Date"
					icon="fa-solid fa-calendar"
					:value="formattedCheckInDate"
					:is-loading="isLoading"
					type="date"
					:skeleton-width="150"
				>
					<template #date-navigation-left>
						<v-btn
							v-if="showDateControl && !isLoading"
							@click="emits('day:minus')"
							size="25"
							color="primary"
							variant="text"
							class="border"
						>
							<v-icon size="14" icon="fa-light fa-arrow-left" />
						</v-btn>
					</template>
					<template #date-navigation-right>
						<v-btn
							v-if="showDateControl && !isLoading"
							@click="emits('day:plus')"
							size="25"
							color="primary"
							variant="text"
							class="border"
						>
							<v-icon size="14" icon="fa-light fa-arrow-right" />
						</v-btn>
					</template>
				</stat-field>

				<!-- Lead Days -->
				<stat-field
					label="Lead Days"
					icon="fa-solid fa-calendar-day"
					:value="store.currentOccupancy?.leadDay"
					:is-loading="isLoading"
					type="lead-days"
					:skeleton-width="120"
				/>

				<!-- Length of Stay -->
				<stat-field
					label="Length of Stay"
					icon="fa-solid fa-arrows-left-right-to-line"
					:value="stats?.los"
					unit="day(s)"
					:is-loading="isLoading"
					type="text"
					:skeleton-width="105"
				/>
			</div>

			<!--	Might be nice to have, but it seems like a bit of a hassle to have-->
			<!--	It also wasn't in use before, so it's commented out for clarity-->

			<!--			<v-chip-->
			<!--				v-if="!isLoading && curveData"-->
			<!--				class="ml-3 mb-3 reference-id-chip"-->
			<!--				rounded="sm"-->
			<!--				size="small"-->
			<!--				variant="tonal"-->
			<!--			>-->
			<!--				<span>-->
			<!--					<b> Reference ID: </b>-->
			<!--					<span>-->
			<!--						{{ curveData.referenceId ?? "N/A" }}-->
			<!--					</span>-->
			<!--				</span>-->
			<!--			</v-chip>-->
		</v-card>

		<v-card class="border" variant="flat">
			<v-card-title class="pa-2 curve-card-header bg-background">
				<div class="curve-title">
					<v-icon
						icon="fa-solid fa-chart-line"
						size="20"
						color="primary"
						class="mr-2"
					/>
					<span class="text-h6 font-weight-medium">Pricing Model</span>
				</div>
			</v-card-title>

			<chart-quick-view
				:is-loading="isLoading"
				:curve-data="curveData"
				:show-border="false"
			/>
		</v-card>

		<template #actions>
			<v-btn color="primary" @click="visible = false">Close</v-btn>
		</template>
	</modal>
</template>

<style scoped>
.curve-card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	height: 42px;
}

.curve-title {
	display: flex;
	align-items: center;
}

.stats-grid {
	display: grid;
	grid-template-columns: 1fr auto;
	gap: 4px 32px;
	margin: 8px 0;
}

.stats-grid > * {
	min-height: 32px;
}

.date-section {
	min-height: 20px;
	min-width: 180px;
	display: flex;
	justify-content: flex-end;
}

.reference-id-chip {
	height: 24px;
}
</style>
